import ATSplitText from '@activetheory/split-text'
import GSAPSplitText from 'gsap/dist/SplitText'
import { forEach } from 'lodash'

export const SplitTextEngine = {
  GSAP: 'GSAP',
  AT: 'AT'
} as const

export type SplitTextEngine = keyof typeof SplitTextEngine
export type SplitTextInstance = GSAPSplitText | ATSplitText
export type SplitTextLines = Element[] | string[]
export type SplitTextStagger = (
  // eslint-disable-next-line no-unused-vars
  stagger: number
) => gsap.StaggerVars | gsap.NumberValue

export type SplitText = {
  instance: SplitTextInstance
  engine: SplitTextEngine
  lines: SplitTextLines
  getStagger: SplitTextStagger
  revert: () => void
}

// Pre-processa l'HTML per estrarre i <br> dai tag inline
// Es: <em>testo<br>altro</em> -> <em>testo</em><br><em>altro</em>
const preprocessBrInInlineTags = (element: HTMLElement): string => {
  const inlineTags = [
    'em',
    'strong',
    'b',
    'i',
    'u',
    'span',
    'a',
    'mark',
    'small',
    'sub',
    'sup'
  ]
  let html = element.innerHTML

  inlineTags.forEach((tag) => {
    // Regex per trovare tag inline che contengono <br>
    const regex = new RegExp(
      `<${tag}([^>]*)>([^<]*(?:<br\\s*\\/?>)[^<]*)+<\\/${tag}>`,
      'gi'
    )

    html = html.replace(regex, (match, attrs) => {
      // Estrai il contenuto del tag
      const contentMatch = match.match(
        new RegExp(`<${tag}[^>]*>([\\s\\S]*?)<\\/${tag}>`, 'i')
      )
      if (!contentMatch) return match

      const content = contentMatch[1]
      // Dividi per <br> e ricostruisci
      const parts = content.split(/<br\s*\/?>/i)

      return parts
        .map((part, index) => {
          const trimmed = part.trim()
          if (!trimmed) return index < parts.length - 1 ? '<br>' : ''
          return `<${tag}${attrs}>${part}</${tag}>${index < parts.length - 1 ? '<br>' : ''}`
        })
        .join('')
    })
  })

  return html
}

// Forza sup/sub a essere inline per evitare che SplitText li metta su righe separate
const forceInlineSupSub = (element: HTMLElement): void => {
  const elements = element.querySelectorAll('sup, sub')
  elements.forEach((el) => {
    const htmlEl = el as HTMLElement
    htmlEl.style.display = 'inline'
    htmlEl.style.verticalAlign = 'baseline'
    htmlEl.style.position = 'relative'
    htmlEl.style.paddingRight = '5px'
    htmlEl.style.fontSize = '0.65em'
    if (el.tagName.toLowerCase() === 'sup') {
      htmlEl.style.top = '-0.5em'
    } else {
      htmlEl.style.top = '0.3em'
    }
  })
}

export const splitLines = (
  target: HTMLElement,
  engine: SplitTextEngine = SplitTextEngine.GSAP
): SplitText => {
  let instance: SplitTextInstance
  const originalHTML = target.innerHTML

  if (engine === 'AT') {
    instance = new ATSplitText(target, {
      type: 'lines,words',
      noBalance: true
    })
  } else {
    // Pre-processa l'HTML per gestire i <br> dentro tag inline
    target.innerHTML = preprocessBrInInlineTags(target)
    // Forza sup/sub inline per evitare split su righe separate
    forceInlineSupSub(target)

    instance = new GSAPSplitText(target, {
      type: 'lines',
      linesClass: 'gsap-line'
    })

    new GSAPSplitText(target, {
      type: 'lines',
      linesClass: 'gsap-line-wrap'
    })
  }

  forEach(instance.lines, (line: any) => {
    if (line.innerHTML.length === 1) {
      line.innerHTML = '&nbsp;'
    }
  })

  const lines: SplitTextLines =
    engine === SplitTextEngine.AT ? instance.words : instance.lines

  const getStagger: SplitTextStagger = (stagger) =>
    engine === SplitTextEngine.AT
      ? (_, target) => stagger * target.__lineIndex
      : { amount: stagger }

  return {
    instance,
    engine,
    lines,
    getStagger,
    revert: () => {
      instance.revert()
      // Ripristina l'HTML originale se era stato preprocessato
      if (engine === SplitTextEngine.GSAP) {
        target.innerHTML = originalHTML
      }
    }
  }
}
